import { ACCT_CONSTANTS, IACCT_CONSTANTS } from '@app/constants';
import { UtilService } from '@app/services/Util.service';
import { Homeowner } from '@app/models/Homeowner';
import { Component, EventEmitter, Inject, Input, Output } from "@angular/core";

export interface PasswordValidationData{
    passwordsMatch: boolean,
    showPasswordInvalidMessage: boolean,
    passwordStrength: string
}

@Component({
    selector: 'app-password',
    template: require('./password.directive.html')
})
export class PasswordComponent {

    @Input() private homeowner: Homeowner;
    @Input() private userPassword = { password: '' };

    @Output() private passwordValidation = new EventEmitter<PasswordValidationData>();

    passwordValidationData: PasswordValidationData;

    confirmUserPassword: string;
    showPassword = false;
    strengthMeterStyle = '';
    strengthTextStyle = '';
    passwordError: string;
    confirmPasswordError: string;
    confirmFocused: boolean;
    passwordContainsFirstName: boolean;
    passwordContainsLastName: boolean;
    passwordContainsUsername: boolean;
    hasPasswordInputBlurred: boolean;

    upperCaseCharExists: boolean;
    lowerCaseCharExists: boolean;
    numericCharExists: boolean;
    specialCharExists: boolean;
    invalidCharExists: boolean;

    protected ACCT_CONSTANTS: IACCT_CONSTANTS = ACCT_CONSTANTS();

    constructor(@Inject(UtilService) private utilService: UtilService) {
        this.passwordValidationData = {
            passwordsMatch: true,
            showPasswordInvalidMessage: false,
            passwordStrength: ''
        }
    }

    analyzePassword() {
        // Get password strength
        this.setProgressClass(this.getPasswordStrength(this.userPassword.password));

        if (this.userPassword.password && this.confirmPassword === null) {
            // do not show invalid message if password is empty
            this.passwordValidationData.showPasswordInvalidMessage = false;
        } else {
            // Password validation
            this.passwordValidationData.showPasswordInvalidMessage =
                !this.isValidPassword(this.userPassword.password,
                    this.confirmUserPassword,
                    this.homeowner.contactInformation.email.value);
        }
        this.passwordValidation.emit(this.passwordValidationData);
    }

    setProgressClass(strength) {
        // Make sure all of the styles are removed
        this.strengthMeterStyle = 'HOA-progress-state-invalid';
        this.strengthTextStyle = '';

        // apply style based on passwordStrength
        if ('Too Short' === strength) {
            this.strengthTextStyle = 'u-fontRed';
            this.strengthMeterStyle = 'HOA-progress-state-too-short';
        } else if ('Weak' === strength) {
            this.strengthTextStyle = 'u-fontRed';
            this.strengthMeterStyle = 'HOA-progress-state-weak';
        } else if ('Fair' === strength) {
            this.strengthTextStyle = 'u-fontOrange';
            this.strengthMeterStyle = 'HOA-progress-state-fair';
        } else if ('Good' === strength) {
            this.strengthTextStyle = 'u-fontLime';
            this.strengthMeterStyle = 'HOA-progress-state-good';
        } else if ('Strong' === strength) {
            this.strengthTextStyle = 'fontLimeDarker';
            this.strengthMeterStyle = 'HOA-progress-state-strong';
        } else if ('Strength' === strength) {
            this.strengthTextStyle = 'empty';
            this.strengthMeterStyle = 'HOA-progress-state-empty';
        } else if ('Invalid Character' === strength) {
            this.strengthTextStyle = 'invalidchar';
            this.strengthMeterStyle = 'HOA-progress-state-invalid';
        }

        this.passwordValidationData.passwordStrength = strength;
    }

    getPasswordStrength(password) {
        /*
         * Length: 13-32 characters. Composition: Allowed characters: Aa-Zz, 0-9, -, (_), /, (), (@), ($), !, (#) passsword cannot
         * equal or contain the userId. minimum of two character classes (lower-alpha, upper-alpha, numeric, special character).
         */

        let strength = null;

        if (password === null || password === undefined || password.length < 1) {
            strength = 'Empty';
            this.upperCaseCharExists = false;
            this.lowerCaseCharExists = false;
            this.numericCharExists = false;
            this.specialCharExists = false;
            this.invalidCharExists = false;
        }

        if (strength === null) {
            // get Character classes
            this.upperCaseCharExists = false;
            this.lowerCaseCharExists = false;
            this.numericCharExists = false;
            this.specialCharExists = false;
            this.invalidCharExists = false;

            for (let x = 0; x < password.length; x++) {
                if (password.charAt(x) >= 'A' && password.charAt(x) <= 'Z') {
                    this.upperCaseCharExists = true;
                } else if (password.charAt(x) >= 'a' && password.charAt(x) <= 'z') {
                    this.lowerCaseCharExists = true;
                } else if (password.charAt(x) >= '0' && password.charAt(x) <= '9') {
                    this.numericCharExists = true;
                } else if (password.charAt(x) === '-' || password.charAt(x) === '_' || password.charAt(x) === '/'
                    || password.charAt(x) === '@' || password.charAt(x) === '$'
                    || password.charAt(x) === '#' || password.charAt(x) === '!') {
                    this.specialCharExists = true;
                } else {
                    this.invalidCharExists = true;
                }
            }

            let strengthVal = 0;
            if (this.upperCaseCharExists) {
                strengthVal++;
            }
            if (this.lowerCaseCharExists) {
                strengthVal++;
            }
            if (this.numericCharExists) {
                strengthVal++;
            }
            if (this.specialCharExists) {
                strengthVal++;
            }
            if (password.length >= 13) {
                strengthVal++;
            }

            if (this.invalidCharExists) {
                strength = 'Invalid Character';
            } else if (password.length < 13) {
                strength = 'Too Short';
            } else if (strengthVal <= 2) {
                strength = 'Weak';
            } else if (strengthVal === 3) {
                strength = 'Fair';
            } else if (strengthVal === 4) {
                strength = 'Good';
            } else {
                // Strength is 5 or greater
                strength = 'Strong';
            }
        }
        this.passwordValidationData.passwordStrength = strength;
        return strength;
    }

    isValidPassword(password, confirmPassword, userId) {
        return this.getInvalidPasswordMsg(password, confirmPassword, userId).length === 0;
    }

    getInvalidPasswordMsg(password, confirmPassword, userId) {
        let invalidPasswordMsg = ''; // NOSONAR: This isn't a password...

        if (password !== confirmPassword) {
            this.passwordValidationData.passwordsMatch = false;
        } else if (password === confirmPassword) {
            this.passwordValidationData.passwordsMatch = true;
        }

        if (this.isEmpty(password) || (confirmPassword && this.isEmpty(confirmPassword))) {
            return this.ACCT_CONSTANTS.FILL_IN_ALL_FIELDS;
        } else if (!this.isValidPasswordNoConfirm(password, userId)) {
            return this.getInitialInvalidPasswordMsg(password, userId);
        }

        return invalidPasswordMsg;
    }

    isEmpty(value) {
        return !(value && value.length > 0);

    }

    isValidPasswordNoConfirm(password, userId) {
        return this.getInitialInvalidPasswordMsg(password, userId).length === 0;
    }

    getInitialInvalidPasswordMsg(password, userId) {
        /*
         * Length: > 13 characters. Composition: Allowed characters: Aa-Zz, 0-9, -, (_), /, (), (@), ($), !, (#) passsword cannot
         * equal or contain the userId. minimum of two character classes (lower-alpha, upper-alpha, numeric, special character).
         *
         * Enterprise LDAP web service will not allow any argument with a wildcard (defined as '*'), so '*' effectively are
         * prohibited from user passwords.
         */
        let invalidPasswordMsg = ''; // NOSONAR: This isn't a password...
        if (password && password.length < 13 && password.length !== 0) {
            invalidPasswordMsg = this.ACCT_CONSTANTS.PW_INVALID_TOO_SHORT;
        }

        // okta - cannot contain first name
        this.passwordContainsFirstName = false;
        if (this.utilService.hasName(password, this.homeowner.firstName)) {
            this.passwordContainsFirstName = true;
            invalidPasswordMsg = this.ACCT_CONSTANTS.PW_INVALID_CONTAINS_FIRST_NAME;
        }

        // okta - cannot contain last name
        this.passwordContainsLastName = false;
        if (this.utilService.hasName(password, this.homeowner.lastName)) {
            this.passwordContainsLastName = true;
            invalidPasswordMsg = this.ACCT_CONSTANTS.PW_INVALID_CONTAINS_LAST_NAME;
        }

        // okta - cannot contain part of username
        this.passwordContainsUsername = false;
        if (this.utilService.hasPartOfEmail(password, userId)) {
            this.passwordContainsUsername = true;
        }

        // validate invalidPasswordMsg - could've been set during char by char check above
        if (this.passwordContainsUsername) {
            invalidPasswordMsg = this.ACCT_CONSTANTS.PW_INVALID_CONTAINS_USER_NAME;
        }

        if (invalidPasswordMsg.length === 0) {
            // get Character classes
            this.upperCaseCharExists = false;
            this.lowerCaseCharExists = false;
            this.numericCharExists = false;
            this.specialCharExists = false;
            this.invalidCharExists = false;

            for (let x = 0; x < password.length; x++) {
                if (password.charAt(x) >= 'A' && password.charAt(x) <= 'Z') {
                    this.upperCaseCharExists = true;
                } else if (password.charAt(x) >= 'a' && password.charAt(x) <= 'z') {
                    this.lowerCaseCharExists = true;
                } else if (password.charAt(x) >= '0' && password.charAt(x) <= '9') {
                    this.numericCharExists = true;
                } else if (password.charAt(x) === '-' || password.charAt(x) === '_' || password.charAt(x) === '/'
                    || password.charAt(x) === '@' || password.charAt(x) === '$' || password.charAt(x) === '#'
                    || password.charAt(x) === '!') {
                    this.specialCharExists = true;
                } else {
                    this.invalidCharExists = true;
                    // Don't need to check the rest of the password at this point
                    // break;
                }
            }
            let charClassCount = 0;
            if (this.upperCaseCharExists) {
                charClassCount++;
            }
            if (this.lowerCaseCharExists) {
                charClassCount++;
            }
            if (this.numericCharExists) {
                charClassCount++;
            }
            if (this.specialCharExists) {
                charClassCount++;
            }
            if (this.invalidCharExists) {
                invalidPasswordMsg = this.ACCT_CONSTANTS.PW_INVALID_CHARACTERS;
            } else if (charClassCount < 4 && password.length !== 0) {
                invalidPasswordMsg = this.ACCT_CONSTANTS.PW_INVALID_ONE_CHAR_CLASS;
            }
        }

        if (invalidPasswordMsg.length === 0) {
            return '';
        } else {
            return invalidPasswordMsg;
        }

    }

    password() {
        if (this.userPassword.password === null)
            this.passwordError = "Please enter your password";
        else
            this.passwordError = null;
    }

    confirmPassword() {
        this.confirmFocused = true;
        if (this.confirmUserPassword === null)
            this.confirmPasswordError = "Please confirm your password";
        else
            this.confirmPasswordError = null;
    }

}

